import React, { useEffect, useMemo, useRef, useState } from "react";

export default function CouncilSelect({ councils, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const chosen = selected || [];

  useEffect(() => {
    if (!open) return undefined;
    function onPointer(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    function onKey(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  function toggle(name) {
    if (chosen.includes(name)) onChange(chosen.filter((item) => item !== name));
    else onChange([...chosen, name]);
  }

  const needle = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!needle) return councils;
    return councils.filter((name) => String(name).toLowerCase().includes(needle));
  }, [councils, needle]);

  const label = chosen.length === 0
    ? "All councils"
    : chosen.length === 1
      ? chosen[0]
      : `${chosen.length} councils`;

  return (
    <div className={`rk-council${open ? " is-open" : ""}${chosen.length ? " has-choice" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="rk-council-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Councils, ${label}`}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>
      {open ? (
        <div className="rk-council-menu" role="listbox" aria-label="Councils" aria-multiselectable="true">
          <div className="rk-council-menu-head">
            <input
              ref={searchRef}
              className="rk-council-search"
              type="search"
              value={query}
              placeholder="Search councils"
              aria-label="Search councils"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
            />
            <button type="button" onClick={() => onChange([])} disabled={!chosen.length}>
              Clear
            </button>
          </div>
          {chosen.length ? (
            <p className="rk-council-menu-note">{chosen.length} selected</p>
          ) : null}
          {visible.length === 0 ? (
            <p className="rk-council-menu-empty">No council matches.</p>
          ) : (
            visible.map((name) => {
              const on = chosen.includes(name);
              return (
                <label key={name} className={on ? "is-on" : ""}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(name)}
                  />
                  <span>{name}</span>
                </label>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
